import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import { useRef } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatFileName } from 'utils/func';
import { REGISTER_RESULT_FILE_TYPE, TOAST } from 'constant';
import { useMutationDeleteRegisterResultProduct } from 'utils/hooks/registerResult';
import { toast } from 'layout';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import ProductDialog from './ProductDialog/index';

export default function ProductDescription({
  disabled,
  control,
  onSubmit,
  registerResultId,
  refetch,
  handleDownloadFile,
}) {
  // #region Data
  const { t } = useTranslation();
  const productDialogRef = useRef(null);

  const { fields, insert, update, remove } = useFieldArray({
    control,
    name: 'registerResultStats',
    keyName: 'customId',
  });

  const { mutateAsync } = useMutationDeleteRegisterResultProduct();

  const accept = async (index) => {
    try {
      await mutateAsync({ registerResultId, productId: fields[index].id });
      remove(index);
      toast(TOAST.SUCCESS, 'Xoá mô tả sản phẩm thành công');
      refetch();
    } catch {
      toast(TOAST.ERROR, 'Xoá mô tả sản phẩm thất bại');
    }
  };

  const confirmDelete = (event, index) => {
    confirmPopup({
      target: event.currentTarget,
      message: 'Bạn có chắc muốn xoá mô tả sản phẩm này không?',
      icon: 'pi pi-exclamation-triangle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      accept: () => accept(index),
      acceptLabel: t('formLayout.action.ok'),
      rejectLabel: t('formLayout.action.cancel'),
    });
  };

  // #endregion Data
  return (
    <div>
      <div className="text-sm font-bold mb-2">
        {t('r09.form.note')}
        <div>{t('r09.form.note1')}</div>
        <div>{t('r09.form.note2')}</div>
      </div>

      <div className="form-table">
        <table className="mb-2">
          <thead>
            <tr>
              <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                {t('formLayout.numericalOrder')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productName')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productDescription')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productCapacity')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t(
                  'registerResult.registerContent.productDescription.fields.productImplementation'
                )}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productEconomy')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productTransmission')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productDocumentType')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.productAddress')}
              </th>
              <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
                {t('registerResult.registerContent.productDescription.fields.evidence')}
              </th>
              {!disabled && (
                <th className="text-center float" style={{ minWidth: 60, width: 60 }}>
                  {t('projectProposal.action')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {fields?.map((item, index) => (
              <tr key={item.id}>
                <td className="text-center">{index + 1}</td>
                <td className="text-center">{item?.name ?? ''}</td>
                <td className="text-center">{item?.description ?? ''}</td>
                <td className="text-center">{item?.productionCapacity ?? ''}</td>
                <td className="text-center">{item?.implementationLevel ?? ''}</td>
                <td className="text-center">{item?.economicData ?? ''}</td>
                <td className="text-center">{item?.transferMethod ?? ''}</td>
                <td className="text-center">{item?.documentType ?? ''}</td>
                <td className="text-center">{item?.address ?? ''}</td>
                <td>
                  {!item?.file?.name ? (
                    item?.filename && (
                      <span
                        className="p-button p-button-text p-button-sm mt-1 p-0"
                        onClick={() => {
                          handleDownloadFile(item?.filename, REGISTER_RESULT_FILE_TYPE.PRODUCTS);
                        }}
                      >
                        <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                        <span className="text-blue-500 underline font-italic ml-1">
                          {formatFileName(item?.filename)}
                        </span>
                      </span>
                    )
                  ) : (
                    <span>{formatFileName(item?.file?.name)}</span>
                  )}
                </td>
                {!disabled && (
                  <td className="text-center px-0 py-2 float">
                    <button
                      className="p-button p-button-text p-button-icon-only p-0"
                      type="button"
                      onClick={(event) => {
                        confirmDelete(event, index);
                      }}
                    >
                      <i
                        className="remove-icon pi pi-times text-red-700 cursor-pointer mb-1"
                        data-pr-tooltip={t('formLayout.action.remove')}
                        data-pr-position="left"
                      />
                    </button>
                    <Tooltip target=".remove-icon" />

                    <div className="">
                      <button
                        className="p-button p-button-text p-button-icon-only p-0"
                        type="button"
                        onClick={() => {
                          productDialogRef.current.open({
                            type: 'UPDATE',
                            index: fields.length,
                            value: item,
                            update,
                          });
                        }}
                      >
                        <i
                          className="update-icon pi pi-pencil text-primary cursor-pointer mt-1"
                          data-pr-tooltip={t('formLayout.action.update')}
                          data-pr-position="left"
                        />
                      </button>
                      <Tooltip target=".update-icon" />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!disabled && (
        <div className="flex justify-content-center my-3">
          <i
            onClick={() => {
              productDialogRef.current.open({
                type: 'INSERT',
                index: fields.length,
                insert: (index, data) => insert(index, data),
              });
            }}
            className="add-recommended-expert pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle"
            data-pr-tooltip={t('formLayout.action.add')}
            data-pr-position="left"
          />
          <Tooltip target=".add-recommended-expert" />
        </div>
      )}
      <ProductDialog
        ref={productDialogRef}
        onSubmit={onSubmit}
        registerResultId={registerResultId}
        refetch={refetch}
      />
      <ConfirmPopup />
    </div>
  );
}

ProductDescription.propTypes = {
  disabled: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  onSubmit: PropTypes.func,
  registerResultId: PropTypes.string,
  refetch: PropTypes.func,
  handleDownloadFile: PropTypes.func,
};

ProductDescription.defaultProps = {
  disabled: false,
  onSubmit: () => null,
  registerResultId: null,
  refetch: () => null,
  handleDownloadFile: () => null,
};
